import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    X, Lock, Unlock, Trash2, GraduationCap, Users, Check,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import ThemedSelect from "./ThemedSelect";

/**
 * Iter-27.55 + 27.58 — Modal de gestion des overrides d'accès (verrouillage
 * personnalisé par utilisateur). Supporte la sélection MULTIPLE de modules
 * en une seule action (bulk apply via `/access-overrides/bulk`).
 *
 * Comportement :
 *   • Sélection 0 module : bouton désactivé
 *   • Sélection 1 module : possibilité d'override leçon spécifique (optionnel)
 *   • Sélection 2+ modules : leçon spécifique masquée, applique à chaque module entier
 */
export default function AccessOverridesModal({ userObj, onClose }) {
    const [overrides, setOverrides] = useState([]);
    const [allModules, setAllModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [selectedModuleIds, setSelectedModuleIds] = useState([]); // Iter-27.58 — multi
    const [lessonId, setLessonId] = useState("");
    const [action, setAction] = useState("unlock");
    const [note, setNote] = useState("");
    const [lessonsOfModule, setLessonsOfModule] = useState([]);

    const reload = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                api.get(`/admin/users/${userObj.user_id}/access-overrides`),
                api.get("/modules"),
            ]);
            setOverrides(r1.data);
            setAllModules(r2.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userObj.user_id]);

    // Si EXACTEMENT 1 module sélectionné → on peut affiner sur une leçon
    const singleModuleId = selectedModuleIds.length === 1 ? selectedModuleIds[0] : null;

    useEffect(() => {
        if (!singleModuleId) {
            setLessonsOfModule([]);
            setLessonId("");
            return;
        }
        api.get(`/modules/${singleModuleId}`).then((r) => {
            setLessonsOfModule(r.data.lessons || []);
        }).catch(() => {
            setLessonsOfModule([]);
        });
        setLessonId("");
    }, [singleModuleId]);

    const toggleModule = (mid) => {
        setSelectedModuleIds((prev) =>
            prev.includes(mid) ? prev.filter((x) => x !== mid) : [...prev, mid]
        );
    };

    const submit = async () => {
        if (selectedModuleIds.length === 0) return;
        setSubmitting(true);
        try {
            if (singleModuleId && lessonId) {
                // Mode classique : 1 module + 1 leçon précise
                await api.post(
                    `/admin/users/${userObj.user_id}/access-overrides`,
                    {
                        module_id: singleModuleId,
                        lesson_id: lessonId,
                        action,
                        note: note.trim() || null,
                    },
                );
                toast.success(action === "unlock" ? "Leçon déverrouillée" : "Leçon verrouillée");
            } else {
                // Mode bulk : 1+ modules entiers
                const res = await api.post(
                    `/admin/users/${userObj.user_id}/access-overrides/bulk`,
                    {
                        module_ids: selectedModuleIds,
                        action,
                        note: note.trim() || null,
                    },
                );
                const n = res.data?.applied ?? selectedModuleIds.length;
                toast.success(
                    action === "unlock"
                        ? `${n} module${n > 1 ? "s" : ""} déverrouillé${n > 1 ? "s" : ""}`
                        : `${n} module${n > 1 ? "s" : ""} verrouillé${n > 1 ? "s" : ""}`,
                );
            }
            setNote("");
            setLessonId("");
            setSelectedModuleIds([]);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    const removeOverride = async (oid) => {
        try {
            await api.delete(`/admin/users/${userObj.user_id}/access-overrides/${oid}`);
            toast.success("Override retiré");
            reload();
        } catch {
            toast.error("Impossible de retirer");
        }
    };

    const { recruiterMods, managerMods } = useMemo(() => {
        const sorted = [...allModules].sort((a, b) => (a.order || 0) - (b.order || 0));
        return {
            recruiterMods: sorted.filter((m) => (m.audience || "recruiter") === "recruiter"),
            managerMods: sorted.filter((m) => m.audience === "manager"),
        };
    }, [allModules]);

    const allIds = useMemo(
        () => [...recruiterMods, ...managerMods].map((m) => m.module_id),
        [recruiterMods, managerMods],
    );
    const allSelected = allIds.length > 0 && selectedModuleIds.length === allIds.length;

    const submitLabel = (() => {
        if (selectedModuleIds.length === 0) return "Choisir au moins un module";
        const n = selectedModuleIds.length;
        if (singleModuleId && lessonId) {
            return action === "unlock"
                ? "Déverrouiller cette leçon"
                : "Verrouiller cette leçon";
        }
        return action === "unlock"
            ? `Déverrouiller ${n} module${n > 1 ? "s" : ""}`
            : `Verrouiller ${n} module${n > 1 ? "s" : ""}`;
    })();

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="access-overrides-modal"
        >
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-shariti-black border border-white/10 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
                <div className="sticky top-0 bg-shariti-black/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            Verrouillage personnalisé
                        </p>
                        <h2 className="text-xl font-extrabold text-white">
                            {userObj.full_name || userObj.email}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="access-overrides-close"
                        className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-6">
                    {/* === FORM : multi-sélection de modules === */}
                    <div className="space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45">
                                    Modules à cibler
                                </label>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="text-[10px] font-extrabold text-shariti-orange-light tabular-nums"
                                        data-testid="override-selected-count"
                                    >
                                        {selectedModuleIds.length} sélectionné
                                        {selectedModuleIds.length > 1 ? "s" : ""}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setSelectedModuleIds(allSelected ? [] : [...allIds])
                                        }
                                        data-testid="override-toggle-all"
                                        className="text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white px-2 py-1 rounded-lg hover:bg-white/5"
                                    >
                                        {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                                    </button>
                                </div>
                            </div>
                            <div
                                className="bg-white/3 border border-white/10 rounded-2xl max-h-72 overflow-y-auto"
                                data-testid="override-modules-picker"
                            >
                                {recruiterMods.length > 0 && (
                                    <div>
                                        <div className="sticky top-0 bg-shariti-black/95 backdrop-blur border-b border-white/5 px-4 py-2 flex items-center gap-2">
                                            <GraduationCap className="w-3.5 h-3.5 text-shariti-orange-light" />
                                            <span className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-shariti-orange-light">
                                                Parcours Recruteur ({recruiterMods.length})
                                            </span>
                                        </div>
                                        <div className="p-1.5">
                                            {recruiterMods.map((m) => (
                                                <ModuleRow
                                                    key={m.module_id}
                                                    module={m}
                                                    selected={selectedModuleIds.includes(m.module_id)}
                                                    onToggle={() => toggleModule(m.module_id)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {managerMods.length > 0 && (
                                    <div>
                                        <div className="sticky top-0 bg-shariti-black/95 backdrop-blur border-b border-white/5 px-4 py-2 flex items-center gap-2">
                                            <Users className="w-3.5 h-3.5 text-shariti-yellow" />
                                            <span className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-shariti-yellow">
                                                Parcours Référent ({managerMods.length})
                                            </span>
                                        </div>
                                        <div className="p-1.5">
                                            {managerMods.map((m) => (
                                                <ModuleRow
                                                    key={m.module_id}
                                                    module={m}
                                                    selected={selectedModuleIds.includes(m.module_id)}
                                                    onToggle={() => toggleModule(m.module_id)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Si 1 seul module → on peut affiner sur une leçon précise */}
                        {singleModuleId && lessonsOfModule.length > 0 && (
                            <div>
                                <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1.5 block">
                                    Leçon spécifique (optionnel)
                                </label>
                                <ThemedSelect
                                    value={lessonId}
                                    onChange={setLessonId}
                                    placeholder="— Tout le module —"
                                    dataTestid="override-lesson-select"
                                    options={[
                                        { value: "", label: "— Tout le module —" },
                                        ...lessonsOfModule.map((l) => ({
                                            value: l.lesson_id,
                                            label: l.title,
                                        })),
                                    ]}
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1.5 block">
                                Action
                            </label>
                            <div className="inline-flex bg-white/5 border border-white/10 rounded-2xl p-1.5 gap-1">
                                <button
                                    onClick={() => setAction("unlock")}
                                    data-testid="override-action-unlock"
                                    className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider inline-flex items-center gap-2 ${
                                        action === "unlock"
                                            ? "bg-shariti-yellow text-shariti-black"
                                            : "text-white/55 hover:text-white"
                                    }`}
                                >
                                    <Unlock className="w-3.5 h-3.5" /> Déverrouiller
                                </button>
                                <button
                                    onClick={() => setAction("lock")}
                                    data-testid="override-action-lock"
                                    className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider inline-flex items-center gap-2 ${
                                        action === "lock"
                                            ? "bg-red-500 text-white"
                                            : "text-white/55 hover:text-white"
                                    }`}
                                >
                                    <Lock className="w-3.5 h-3.5" /> Verrouiller
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-1.5 block">
                                Note (motif visible)
                            </label>
                            <input
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Ex: Accès anticipé · brief manager"
                                maxLength={200}
                                data-testid="override-note"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/50"
                            />
                        </div>

                        <button
                            onClick={submit}
                            disabled={selectedModuleIds.length === 0 || submitting}
                            data-testid="override-submit"
                            className={`w-full font-extrabold py-3 rounded-2xl transition-all ${
                                action === "lock"
                                    ? "bg-red-500 text-white hover:bg-red-600"
                                    : "bg-shariti-yellow text-shariti-black hover:bg-shariti-yellow/90"
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                            {submitting ? "…" : submitLabel}
                        </button>
                    </div>

                    {/* === LISTE des overrides actifs === */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/45">
                                Overrides actifs
                            </p>
                            <span className="text-[10px] font-bold text-white/65 tabular-nums">
                                {overrides.length}
                            </span>
                        </div>

                        {loading ? (
                            <p className="text-white/40 text-sm py-6 text-center">Chargement…</p>
                        ) : overrides.length === 0 ? (
                            <p
                                className="text-white/35 text-sm py-6 text-center"
                                data-testid="overrides-empty"
                            >
                                Aucun override actif. Cet utilisateur suit la logique standard.
                            </p>
                        ) : (
                            <div className="space-y-2" data-testid="overrides-list">
                                {overrides.map((o) => {
                                    const isLock = o.action === "lock";
                                    const isManager = o.module_audience === "manager";
                                    return (
                                        <div
                                            key={o.override_id}
                                            data-testid={`override-row-${o.override_id}`}
                                            className={`flex items-start gap-3 p-3 rounded-2xl border ${
                                                isLock
                                                    ? "bg-red-500/8 border-red-500/30"
                                                    : "bg-shariti-yellow/8 border-shariti-yellow/30"
                                            }`}
                                        >
                                            <div
                                                className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
                                                    isLock ? "bg-red-500/20" : "bg-shariti-yellow/20"
                                                }`}
                                            >
                                                {isLock ? (
                                                    <Lock className="w-4 h-4 text-red-300" />
                                                ) : (
                                                    <Unlock className="w-4 h-4 text-shariti-yellow" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-extrabold text-white truncate">
                                                        {o.module_title}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/8 text-white/65">
                                                        {isManager ? (
                                                            <Users className="w-2.5 h-2.5" />
                                                        ) : (
                                                            <GraduationCap className="w-2.5 h-2.5" />
                                                        )}
                                                        {isManager ? "Référent" : "Recruteur"}
                                                    </span>
                                                </div>
                                                {o.lesson_title && (
                                                    <p className="text-[11px] text-white/55 mt-0.5">
                                                        Leçon : {o.lesson_title}
                                                    </p>
                                                )}
                                                {o.note && (
                                                    <p className="text-[11px] text-white/45 italic mt-1">
                                                        « {o.note} »
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => removeOverride(o.override_id)}
                                                data-testid={`override-delete-${o.override_id}`}
                                                title="Retirer cet override"
                                                className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/55 hover:text-red-300 shrink-0"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}

function ModuleRow({ module, selected, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            data-testid={`override-module-row-${module.module_id}`}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                selected
                    ? "bg-shariti-orange/15 text-white"
                    : "text-white/85 hover:bg-white/5 hover:text-white"
            }`}
        >
            <div
                className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                    selected
                        ? "bg-shariti-orange border-shariti-orange"
                        : "bg-transparent border-white/25"
                }`}
            >
                {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </div>
            <span className="text-sm font-bold truncate">{module.title}</span>
        </button>
    );
}
